"use client";

import { SanityDocument } from "next-sanity";
import React from "react";
import SectionHeader from "./section-header";
import { urlFor } from "@/sanity/lib/image";
import clsx from "clsx";
import Marquee from "../ui/marquee";
import Image from "next/image";
import { Card, CardContent } from "../ui/card";
import { ArrowRight } from "lucide-react";
import { splitArrayIntoThree } from "@/lib/splitArrayIntoThree";
import { animatePageOut } from "@/lib/pageAnimate";
import { useRouter } from "next/navigation";
import { useMediaQuery } from "@/hooks/useMediaQuery";

interface ProjectSectionProps {
  projects: SanityDocument[];
}

function ProjectSection({ projects }: ProjectSectionProps) {
  const router = useRouter();
  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const marqueeColumns = splitArrayIntoThree(projects);

  const handleClick = (href: string) => {
    const url = `projects/${href}`;
    animatePageOut(url, router);
  };

  return (
    <section
      className={clsx(
        "relative flex min-h-screen w-full flex-col gap-5 bg-gradient-to-b from-slate-50 to-white px-5 pb-16 pt-8 font-Satoshi-Medium text-base md:px-10 md:text-lg lg:py-20",
      )}
    >
      <SectionHeader
        title="Built with Precision, Backed by Expertise!"
        highlightedTitle="Our Projects: "
        bodyText="At Rake Engineering Consultancy, our projects are more than just structures—they’re a testament to smart design, technical excellence, and unwavering dedication. Each project showcases our ability to deliver tailored engineering and consultancy solutions that meet real-world challenges and exceed expectations."
      />
      <div className="relative mx-auto flex h-full max-h-screen min-h-72 w-full min-w-72 max-w-7xl flex-col items-center justify-center overflow-hidden xl:flex-row">
        <div className="pointer-events-none absolute inset-0 z-20 flex justify-between">
          <div className="h-full w-16 bg-gradient-to-r from-white to-transparent xl:h-16 xl:w-full xl:bg-gradient-to-b" />
          <div className="h-full w-16 bg-gradient-to-l from-white to-transparent xl:absolute xl:bottom-0 xl:h-16 xl:w-full xl:bg-gradient-to-t" />
        </div>
        {marqueeColumns.map((rows, index) => (
          <Marquee
            key={`marquee-${index}`}
            className="items-center"
            vertical={isDesktop ?? true}
            pauseOnHover
            reverse={index % 2 === 0}
          >
            {rows.map((item) => (
              <Card
                key={item._id}
                onClick={() => handleClick(item.slug.current)}
                className="max-w-96 cursor-pointer rounded-none p-0 xl:max-w-[400px]"
              >
                <CardContent className="group relative aspect-[5/4] h-auto min-h-52 max-w-full overflow-hidden rounded-none p-0 lg:aspect-[16/9] lg:h-full lg:min-h-64 xl:min-h-[27rem]">
                  <Image
                    width={1000}
                    height={1000}
                    src={urlFor(item.gallery[0]).url()}
                    alt={item.gallery[0].alt ?? "Service Image"}
                    className="h-full object-cover blur-sm brightness-50 grayscale-[50%] filter-none group-hover:filter"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 h-full bg-[linear-gradient(hsl(var(--primary)/0),hsl(var(--primary)/0.4),hsl(var(--primary)/0.8)_100%)] mix-blend-multiply" />
                  <div className="absolute inset-x-0 bottom-0 flex flex-col items-start p-6 text-primary-foreground md:p-8">
                    <div className="mb-2 pt-4 text-lg font-semibold md:mb-3 md:pt-4 md:text-xl lg:pt-4">
                      {item.title}
                    </div>
                    <div className="mb-8 line-clamp-2 text-sm md:mb-12 md:text-base lg:mb-9">
                      {item.description}
                    </div>
                    <div className="flex items-center text-sm">
                      <span className="text-base">Read more</span>
                      <ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </Marquee>
        ))}
      </div>
    </section>
  );
}

export default ProjectSection;
